import { useState } from "react";
import { toast } from "sonner";
import type { OrderDto } from "@/shared/api/types";
import { describeError } from "@/shared/api/errors";
import { useMe } from "@/shared/auth/session";
import { OrderDialog } from "@/shared/components/OrderDialog";
import { SupportBanner } from "@/shared/components/SupportBanner";
import { TenantStateBanner, type TenantBannerState } from "@/shared/components/TenantStateBanner";
import { useBillingActionsPermission, useResumeSubscription } from "@/shared/hooks/useBillingActions";
import { useBillingSummaryQuery } from "@/shared/hooks/useBillingSummary";
import { useCancelDelete } from "@/shared/hooks/useEntitlements";
import { useOrgTimezone } from "@/shared/hooks/useOrgTimezone";
import { usePermissions } from "@/shared/hooks/usePermissions";
import { replacingOf, useStaleRenewal } from "@/shared/hooks/useStaleRenewal";
import { useTenantBannerStates } from "@/shared/hooks/useTenantBannerStates";
import { LiveLimitBanner } from "@/shared/live/LiveProvider";

/**
 * Organisation-state notices under the top bar: a support session (from /me), a scheduled deletion, the
 * trial clocks and anything owed (M11 §6, ADR-0015 §5). Suspension is an interstitial, not a banner
 * (AppShell), because at that point reads are refused too.
 *
 * **Which notices are owed is not decided here** (redesign 2026-09, pass 03): `useTenantBannerStates`
 * is the one reading, and the billing strip at the foot of the page stands down on the same boolean.
 * This component owns only what a selector cannot — the mutations, their toasts and the pay dialog —
 * which it hangs off the states on the way past.
 */
export function ShellBanners() {
  const me = useMe();
  const tz = useOrgTimezone();
  const { can } = usePermissions();
  const canManage = useBillingActionsPermission();
  const cancel = useCancelDelete();
  const resume = useResumeSubscription();
  // The dialog's own read: the payment mode and the bounded poll that follows a hosted page. The
  // query is the same cached one the selector makes, so this costs nothing extra.
  const billing = useBillingSummaryQuery(can("billing", "view"));
  const { banners, openOrder } = useTenantBannerStates();
  // The banner can open the renewal invoice while the worker is replacing it (ADR-0015 addendum of
  // 2026-10-04): the dialog says so and withholds Pay, exactly as the Subscription page does.
  const staleRenewal = useStaleRenewal(billing);
  const [order, setOrder] = useState<OrderDto | null>(null);

  const onCancel = () => {
    cancel.mutate(undefined, {
      onSuccess: () => toast.success("Deletion cancelled", { description: "Nothing was removed." }),
      onError: (error) => {
        const { title, detail } = describeError(error);
        toast.error(title, detail ? { description: detail } : undefined);
      },
    });
  };

  const onResume = () => {
    resume.mutate(undefined, {
      onSuccess: () => toast.success("Subscription resumed", { description: "It renews as usual." }),
      onError: (error) => {
        const { title, detail } = describeError(error);
        toast.error(title, detail ? { description: detail } : undefined);
      },
    });
  };

  /** The one thing to do about a state, attached per kind; the state itself carries only facts. */
  const withAction = (state: TenantBannerState): TenantBannerState => {
    switch (state.kind) {
      case "deleting":
        return { ...state, onCancel, cancelling: cancel.isPending };
      case "subscription_ending":
        return { ...state, onResume, resuming: resume.isPending };
      case "invoice_issued":
      case "overdue":
        return openOrder ? { ...state, onPay: () => setOrder(openOrder) } : state;
      default:
        return state;
    }
  };

  return (
    <>
      <LiveLimitBanner />
      {me.support ? (
        <SupportBanner operatorName={me.support.operatorName} expiresAt={me.support.expiresAt} tz={tz} />
      ) : null}
      {banners.map((state) => (
        <TenantStateBanner key={state.kind} state={withAction(state)} tz={tz} canManage={canManage} />
      ))}
      <OrderDialog
        order={order}
        onOpenChange={(isOpen) => (!isOpen ? setOrder(null) : undefined)}
        tz={tz}
        mode={billing.data?.mode ?? "razorpay"}
        replacing={replacingOf(order, staleRenewal)}
        onPaying={() => billing.watchForChange()}
      />
    </>
  );
}
